import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import {
  setCompleteTablePageNo,
  setCompleteTableSearchKey,
} from '../../commonSlice.js';
import ButtonComponent from '../ButtonComponent.jsx';
import { actionPlanFilterInt } from '../constant.js';
import CustomSelectSearch from '../custom-select-with-search/index.jsx';
import MoreFilter from '../CustomMoreFilter/moreFilter.jsx';
import CustomSelect from '../select/index.jsx';
import Spinners from '../Spinner.jsx';
import { getTooltipOverlay } from './Helpers.jsx';
import PaginationControls from './PaginationControls.jsx';
import styles from "./Table.module.scss";

function CompleteTableData({
  rowWidth,
  customSearchClass,
  DropData,
  moveFilterToStart,
  moveSearchToEnd,
  onScroll,
  data,
  issearch = false,
  columns,
  isLoading,
  isMailRequired,
  pageLimit,
  onSorting,
  headerSortingState,
  onPageLimitChange,
  listCount,
  onPageNoChange,
  curentPageNumber,
  onSearch,
  searchKey,
  btnData,
  filtersToShow,
  showFilter,
  tbodyClass,
  onFilter,
  isTableFilterDisabled,
  additionalBtnData,
  scrollClass,
  filterOptionToHide,
  ismore,
  optionList,
  handleOptionClick,
  selectedOption,
  isApply,
  requiredOptions,
  highlightRow,
  selectedRow,
  isNotShowMoreFilter,
  isSlNoRequired,
  noDataMessage,
  manageBtnData,
  removeHoveringEffect,
  disableSorting,
  numberOfColumnsToFix,
  UARSearch,
  removePosition,
  placement,
  tableStyle,
  tableHeaderStyle,
  ColumnCustomizeClsName,
  TableTitle,
  checkboxConfig = null,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    previousPage,
    nextPage,
    gotoPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const dispatch = useDispatch();
  const [searchInputValue, setSearchInputValue] = useState(
    searchKey ? searchKey : ""
  );
  const [stickyLeftOffsets, setStickyLeftOffsets] = useState([]);
  const [tooltipPlacement, setTooltipPlacement] = useState("top");
  const headerRefs = useRef([]);
  const tableRef = useRef(null);

  const isAllRowsDisabled = isLoading
    || data.length === 0
    || (checkboxConfig?.isRowSelectable && data.every(row => !checkboxConfig?.isRowSelectable(row)));

  const calculateStickyOffsets = () => {
    // Skip if the first fixed column header has no width — table is hidden
    // (e.g. inactive React Bootstrap tab with display:none). The ResizeObserver
    // will trigger a recalculation when the table becomes visible.
    if (numberOfColumnsToFix > 0 && headerRefs.current[0]?.offsetWidth === 0) return;

    const offsets = [];
    let currentOffset = 0;

    // Use tableRef to scope queries to this specific table instance
    // (avoids picking up elements from other table instances in hidden tabs)
    const container = tableRef.current;

    // Account for serial number column if present
    if (isSlNoRequired) {
      const slNoElement = container
        ? container.querySelector(`.${styles["th-slno"]}`)
        : document.querySelector(`.${styles["th-slno"]}`);
      if (slNoElement) {
        currentOffset = slNoElement.offsetWidth;
      }
    }

    // Account for checkbox column if present
    if (checkboxConfig) {
      const checkboxElement = container
        ? container.querySelector(`.${styles["th-checkbox"]}`)
        : document.querySelector(`.${styles["th-checkbox"]}`);
      if (checkboxElement) {
        currentOffset += checkboxElement.offsetWidth;
      }
    }

    headerRefs.current.forEach((ref, index) => {
      if (index < numberOfColumnsToFix && ref) {
        offsets.push(currentOffset);
        currentOffset += ref.offsetWidth;
      }
    });
    setStickyLeftOffsets(offsets);
  };

  useLayoutEffect(() => {
    calculateStickyOffsets();
  }, [columns, data, numberOfColumnsToFix, isSlNoRequired, !!checkboxConfig]);

  useEffect(() => {
    // Recalculate offsets on window resize (for sidebar expand/collapse)
    let rafId = null;
    const debouncedCalculate = () => {
      if (rafId) { cancelAnimationFrame(rafId); }
      rafId = requestAnimationFrame(() => {
        calculateStickyOffsets();
      });
    };

    window.addEventListener('resize', debouncedCalculate);

    // Use ResizeObserver to detect table container size changes
    const resizeObserver = new ResizeObserver(debouncedCalculate);

    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      if (rafId) { cancelAnimationFrame(rafId); }
      window.removeEventListener('resize', debouncedCalculate);
      resizeObserver.disconnect();
    };
  }, [columns, data, numberOfColumnsToFix, isSlNoRequired]);

  const handleInputChange = (e) => {
    const newValue = e.target.value.trimStart();
    setSearchInputValue(newValue);
    if (e.target.value === "") {
      if (newValue !== searchKey) {
        onSearch(newValue);
      }
    }
  };

  const handleSearchClick = () => {
    const searchValue = searchInputValue;
    onSearch(searchInputValue);
    setGlobalFilter(searchValue);
    dispatch(setCompleteTablePageNo(0));
    dispatch(setCompleteTableSearchKey(searchInputValue));
    gotoPage(0);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && searchInputValue.length > 1) {
      handleSearchClick();
    }

    // Trigger search when Space is pressed with selected text
    if ((event.key === " ") && searchInputValue.length > 0) {
      const input = event.target;
      const hasSelection = input.selectionStart !== input.selectionEnd;

      if (hasSelection) {
        event.preventDefault();
        setSearchInputValue("");
        onSearch("");
        setGlobalFilter("");
        dispatch(setCompleteTableSearchKey(""));
      }
    } else if (event.key === " " && searchInputValue.length === 0) {
      event.preventDefault();
    }
  };


  useEffect(() => {
    if (searchInputValue === "") {
      dispatch(setCompleteTableSearchKey(""));
    }
  }, [searchInputValue]);

  useEffect(() => {
    setSearchInputValue(searchKey);
  }, [searchKey]);

  const getFilterOptions = () => {
    let returnObj = _.map(filtersToShow, (obj) => {
      if (obj.key == filterOptionToHide) {
        return null;
      }
      return (
        <div className={styles.srvrTableFilters}>
          <CustomSelect
            withSelectAll={true}
            options={obj.options}
            isMulti
            hideSelectedOptions={false}
            isCheckboxSelect={true}
            placeholder={obj.placeholder}
            isSearchable={true}
            closeMenuOnSelect={false}
            defaultValue={obj.defaultValue}
            onChange={(e) => obj.changeFunc(e, obj.key)}
            value={obj.value}
            searchPlaceHolder={obj.searchPlaceHolder}
          />
        </div>
      );
    });
    return returnObj;
  };

  const removeSortButton = () => {
    const tableRemoveSort = tableRef.current?.querySelector(".table-hover")
      || tableRef.current?.closest(".table-hover");

    if (tableRemoveSort) {
      const tableHeaders = tableRemoveSort.querySelectorAll("th");

      tableHeaders.forEach((header) => {
        const spanElement = header.querySelector("span");

        if (spanElement && !header.hasAttribute("title")) {
          spanElement.style.display = "none";
        }
      });
    }
  };

  const handleOnSorting = (column, headerSortingState) => {
    // Check if column.parent is defined
    if (column.parent && column.parent.columns) {
      // Find the object in column.parent.columns that matches column.header
      const matchingColumn = column.parent.columns.find(
        (col) => col.Header === column.Header
      );
      // Check if a matching column object was found
      if (matchingColumn) {
        onSorting(matchingColumn, headerSortingState);
      }
    } else {
      onSorting(column, headerSortingState);
    }
  };

  const renderSearch = () => {
    if (!issearch) {
      return (
        <li
          className={`input-group serverInput searchStyle manageVulStyle searchmargin ${customSearchClass}`}
        >
          <input
            id="searchInput"
            type="text"
            className={`form-control ${styles["search-input"]}`}
            data-testid="tableSearchInput"
            placeholder="Search"
            value={searchInputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <button
            className={`btn ${styles["search-btn"]}`}
            data-testid="tableSearchButton"
            onClick={() => {
              if (searchInputValue) {
                handleSearchClick();
              }
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.0168 1.71622C7.7285 -0.572073 4.00414 -0.572073 1.71585 1.71622C-0.571949 4.00501 -0.571949 7.72888 1.71585 10.0177C3.75363 12.055 6.92738 12.2732 9.21418 10.6824C9.2623 10.9101 9.37242 11.1273 9.54951 11.3044L12.882 14.6369C12.9971 14.7521 13.1338 14.8436 13.2842 14.9059C13.4346 14.9683 13.5959 15.0004 13.7588 15.0004C13.9216 15.0004 14.0829 14.9683 14.2333 14.9059C14.3838 14.8436 14.5205 14.7521 14.6355 14.6369C14.7509 14.5219 14.8424 14.3852 14.9049 14.2348C14.9673 14.0843 14.9995 13.923 14.9995 13.7601C14.9995 13.5972 14.9673 13.4359 14.9049 13.2855C14.8424 13.135 14.7509 12.9984 14.6355 12.8834L11.3031 9.54989C11.1326 9.38059 10.9167 9.26444 10.6815 9.21555C12.2733 6.92825 12.0551 3.755 10.0168 1.71622ZM8.96467 8.96554C7.25626 10.6739 4.47588 10.6739 2.76797 8.96554C1.06056 7.25713 1.06056 4.47725 2.76797 2.76884C4.47588 1.06093 7.25626 1.06093 8.96467 2.76884C10.6731 4.47725 10.6731 7.25713 8.96467 8.96554Z"
                fill="#595959"
              />
            </svg>
          </button>
        </li>
      );
    }
    return null;
  };

  const renderButtons = () => {
    if (btnData) {
      return btnData.map((btn, index) => (
        <li className={styles.manageVulStyle} key={index}>
          <a className={`nav-link p-0 statusFilter ${btn?.ContainerClassName || ""}`} data-testid={`btn-${btn.text}`}
          >
            {btn.btnComponent ? (
              btn.btnComponent
            ) : (
              <Button
                className={btn.className}
                data-testid={`button-${btn.text}`}
                onClick={btn.function}
                disabled={btn.disabled || false}
              >
                {btn.text}
              </Button>
            )}
          </a>
        </li>
      ));
    }
    return null;
  };

  const renderDropdown = () => {
    if (DropData) {
      return DropData.map((btn, index) => (
        <ul key={index}>
          <li className="">{btn.btnComponent && btn.btnComponent}</li>
        </ul>
      ));
    }
    return null;
  };

  const updateTooltipPlacement = (elementRef) => {
    if (!elementRef) {
      return;
    }
    const rect = elementRef.getBoundingClientRect();
    const spaceAbove = rect.top;
    // Only switch to bottom if top space is too small (e.g., less than 100px)
    if (spaceAbove > 100) {
      setTooltipPlacement("top");
    } else {
      setTooltipPlacement("bottom");
    }
  };

  return (
    <>
      <div
        className={`justify-content-between ${styles.tableStyle} ${tableStyle}`}
      >
        <div
          className={`${styles["Table-Header"]} ${tableHeaderStyle}`}
          data-testid="TableDataHeaderDiv"
        >
          <div className="">
            {renderDropdown && <div>{renderDropdown()}</div>}
            {TableTitle && <h4 className={styles.tableTitle}>{TableTitle}</h4>}
          </div>
          <div>
            <ul
              className={`${styles.checkStyle} ${styles["additional-btn"]} ${styles.tableBtn
                } ${UARSearch ? UARSearch : ""}`}
            >
              {moveSearchToEnd ? renderSearch() : renderButtons()}
              {moveSearchToEnd ? renderButtons() : renderSearch()}
              {additionalBtnData
                ? additionalBtnData.map((btn, index) => (
                  <li key={index}>{btn.btnComponent}</li>
                ))
                : null}
              {ismore && (
                <li className="">
                  <MoreFilter
                    data-testid="more-filter"
                    name={"More Fields"}
                    optionList={optionList}
                    handleOptionClick={handleOptionClick}
                    selectedOptionlist={selectedOption}
                    tooltip={true}
                    tooltipText={
                      "Click here for more fields in the table below."
                    }
                    disabled={isTableFilterDisabled}
                  />
                  {isMailRequired && (
                    <>
                      <li className="nav-item mx-2">
                        <a
                          className={`nav-link invisible ${styles["custom-padding-nav"]}`}
                        >
                          <CustomSelectSearch
                            options={actionPlanFilterInt}
                            defaultValue={actionPlanFilterInt[0]}
                            placeholder="Select Action Plan"
                            className="text-nowrap"
                            name="Action Plan"
                            data-testid="action-plan-select"
                          />
                        </a>
                      </li>
                      <li className="nav-item mx-2">
                        <a
                          className={`nav-link invisible ${styles["custom-padding-nav"]}`}
                        >
                          <Button className={`text-nowrap manageEnabled`}
                            data-testid="manage-vulnerability">
                            Manage Vulnerability
                          </Button>
                        </a>
                      </li>
                    </>
                  )}
                </li>
              )}
              {manageBtnData
                ? manageBtnData.map((btn, index) => (
                  <li className={styles.manageStyl} key={index}>
                    <a
                      className={`nav-link p-0 ${styles.statusFilter} ${btn.ContainerClassName}`}
                    >
                      {btn.btnComponent ? (
                        btn.btnComponent
                      ) : (
                        <Button
                          className={btn.className}
                          onClick={btn.function}
                          disabled={btn.disabled || false}
                        >
                          {btn.text}
                        </Button>
                      )}
                    </a>
                  </li>
                ))
                : null}
            </ul>
          </div>
        </div>

        {showFilter && (
          <div
            className={`${styles.tableFilters
              } d-flex justify-content-end mb-2 ${moveFilterToStart && styles["filter_to_left"]
              }`}
          >
            <Row className="mx-0">
              <Col className="ColSpace">
                <div className="d-flex float-right">{getFilterOptions()}</div>
              </Col>
              <Col className="ColSpace">
                <div className="d-flex justify-content-end">
                  <ButtonComponent
                    text="Filter"
                    className={"solid-btn btn btn-primary"}
                    data-testid="tableFilterButton"
                    disabled={isTableFilterDisabled}
                    onClick={onFilter}
                  />
                </div>
              </Col>
            </Row>
          </div>
        )}
        {ismore ? (
          <div
            className={`${styles.tableFilters} d-flex justify-content-end mb-2`}
          >
            <Row className="mx-1">
              <Col className={styles.padForFilter}>
                <div className="d-flex float-right">{getFilterOptions()}</div>
              </Col>
              <Col className={`${styles.padForFilter} ${styles.marForFilter}`}>
                <div className="d-flex justify-content-end">
                  <>
                    {isNotShowMoreFilter && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{"Click here for more filters"}</Tooltip>
                        }
                      >
                        <span className="d-inline-block" tabIndex="0">
                          <ButtonComponent
                            text="More Filters"
                            className={`${styles.detailsFilterStyle} mx-1`}
                            onClick={isApply && isApply}
                            disabled={requiredOptions}
                          />
                        </span>
                      </OverlayTrigger>
                    )}
                  </>
                </div>
              </Col>
            </Row>
          </div>
        ) : null}
        <div
          onScroll={onScroll}
          ref={tableRef}
          className={`${styles.tableBorderRadiusCustomScroll
            } table-responsive custom_scroll w-100 bg-white ${scrollClass ? scrollClass : ""
            }`}
          data-testid="completeTableDataDiv"
        >
          <table
            {...getTableProps()}
            className={`${styles.custom_table_vul} table ${styles.serverTable} mb-0`}
          >
            <thead
              className={
                !removePosition ? styles["sticky-centered-header"] : ""
              }
            >
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {isSlNoRequired && (
                    <th
                      className={styles["th-slno"]}
                      style={{
                        position: "sticky",
                        left: "0px",
                        backgroundColor: "#dcdfdd",
                        zIndex: 1000,
                      }}
                    >
                      Sl.No
                    </th>
                  )}
                  {checkboxConfig && (
                    <OverlayTrigger
                      placement={placement || tooltipPlacement}
                      overlay={
                        <Tooltip className="tooltipHeader tableToolTipBody">
                          {checkboxConfig?.headerTooltip
                            ? checkboxConfig?.headerTooltip
                            : "Select All will be disabled if items are from different categories"}
                        </Tooltip>
                      }
                      trigger={checkboxConfig?.showHeaderTooltip === false ? [] : ["hover", "focus"]}
                    >
                      <th
                        onMouseMove={(e) => updateTooltipPlacement(e.currentTarget)}
                        className={`${styles["th-checkbox"]}${numberOfColumnsToFix > 0
                            ? ` ${styles["th-checkbox-fixed"]} ${isSlNoRequired
                              ? styles["th-checkbox-with-slno"]
                              : styles["th-checkbox-without-slno"]
                            }`
                            : ""
                          }`}
                      >
                        <div className="center-checkbox">
                          <input
                            id="checkbox-header"
                            type="checkbox"
                            data-testid="checkAll"
                            checked={checkboxConfig?.isHeaderChecked || false}
                            disabled={checkboxConfig?.isHeaderDisabled || isAllRowsDisabled || false}
                            onChange={(e) => checkboxConfig?.onSelectAll && checkboxConfig?.onSelectAll(e.target.checked)}
                          />
                        </div>
                      </th>
                    </OverlayTrigger>
                  )}
                  {headerGroup.headers.map((column, i) => {
                    return (
                      <OverlayTrigger
                        key={i}
                        placement={placement || tooltipPlacement}
                        overlay={getTooltipOverlay(column)}
                        trigger={["hover", "focus"]}
                        rootClose
                      >
                        <th
                          {...column.getHeaderProps()}
                          ref={(el) => (headerRefs.current[i] = el)}
                          onMouseMove={() =>
                            updateTooltipPlacement(headerRefs.current[i])
                          }
                          colSpan={column.columns ? column.columns.length : 1}
                          title=""
                          style={{
                            position:
                              i < numberOfColumnsToFix ? "sticky" : undefined,
                            left:
                              i < numberOfColumnsToFix && stickyLeftOffsets[i] != null
                                ? `${stickyLeftOffsets[i]}px`
                                : undefined,
                            zIndex: i < numberOfColumnsToFix ? 1000 : undefined,
                            backgroundColor: "#dcdfdd",
                            margin: "0px",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                            ...column.thHeaderStyle,
                            cursor:
                              !column.disableSortBy && headerSortingState
                                ? "pointer"
                                : "default",
                          }}
                          className={`${column.thClassName}  align-middle ${typeof column.render("Header") === "object"
                            ? column.className || "noSort"
                            : ""
                            } ${i < numberOfColumnsToFix
                              ? styles.stickyColumnHeader
                              : `${styles.nonStickyColumnHeader} ${column.thColumnCustomizeClsName || ""}`
                            } ${i === numberOfColumnsToFix
                              ? styles.firstNonStickyHeader
                              : ""
                            }`}
                          {...(column.columns ? removeSortButton() : "")}
                          data-testid={`tableHeaderCell-${column.id || i}`}
                        >
                          <div
                            onClick={() =>
                              !column.disableSortBy &&
                              !disableSorting &&
                              onSorting &&
                              handleOnSorting(column, headerSortingState)
                            }
                          >
                            {column.render("Header")}
                            {!column.disableSortBy && !disableSorting && (
                              <span>
                                {column.isSorted ||
                                  column.id === headerSortingState?.accessor ? (
                                  column.isSortedDesc ||
                                    headerSortingState?.isDataSorted === "asc" ? (
                                    <FaSortUp
                                      className={styles["sorted-icon"]}
                                    />
                                  ) : (
                                    <FaSortDown
                                      className={styles["sorted-icon"]}
                                    />
                                  )
                                ) : (
                                  <FaSort
                                    className={`${styles["sort-icon"]} ${styles["sort-desc"]}`}
                                  />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      </OverlayTrigger>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody
              {...getTableBodyProps()}
              className={`align-middle ${tbodyClass}`}
            >
              {isLoading ? (
                <tr>
                  <td
                    colSpan={
                      headerGroups.reduce(
                        (count, headerGroup) =>
                          count + headerGroup.headers.length,
                        0
                      ) + (isSlNoRequired ? 1 : 0) + (checkboxConfig ? 1 : 0)
                    }
                    className={`${ColumnCustomizeClsName} projectemty text-center p-0`}
                  >
                    <Spinners />
                  </td>
                </tr>
              ) : rows.length !== 0 ? (
                rows.map((row, index) => {
                  prepareRow(row);
                  const serialNumber = curentPageNumber * pageLimit + index + 1;
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={row.id}
                      className={rowWidth}
                      data-testid={`tableRow-${index}`}
                    >
                      {isSlNoRequired && (
                        <td
                          className={`text-center ${styles["slno-td"]}`}
                          style={{
                            position: "sticky",
                            left: "0px",
                            backgroundColor: "#fff",
                            zIndex: 0,
                          }}
                        >
                          {serialNumber}
                        </td>
                      )}
                      {checkboxConfig && (
                        <td
                          className={`text-center ${styles["checkbox-td"]}${numberOfColumnsToFix > 0
                              ? ` ${styles["checkbox-td-fixed"]} ${isSlNoRequired
                                ? styles["checkbox-td-with-slno"]
                                : styles["checkbox-td-without-slno"]
                              }`
                              : ""
                            }`}
                        >
                          <div className="center-checkbox">
                            <input
                              id={`checkbox-row-${row.original.id}`}
                              type="checkbox"
                              data-testid="rowCheck"
                              checked={checkboxConfig?.isRowChecked ? checkboxConfig?.isRowChecked(row?.original) : false}
                              disabled={checkboxConfig?.isRowSelectable ? !checkboxConfig?.isRowSelectable(row?.original) : false}
                              onChange={(e) => checkboxConfig?.onRowSelect && checkboxConfig?.onRowSelect(row?.original, e.target.checked)}
                            />
                          </div>
                        </td>
                      )}
                      {row.cells.map((cell, i) => {
                        const isLastSticky = i === numberOfColumnsToFix - 1;
                        return (
                          <td
                            key={i}
                            {...cell.getCellProps()}
                            className={
                              highlightRow &&
                                _.isEqual(selectedRow, row.original)
                                ? `${cell.column.tdClassName}  ${styles.highlightRow} ${styles.cellBorder}`
                                : `${cell.column.tdClassName} ${i < numberOfColumnsToFix
                                  ? `${i === 0 && (checkboxConfig || isSlNoRequired)
                                      ? styles.stickyColumnCellNoLeft
                                      : styles.stickyColumnCell
                                    } ${isLastSticky ? styles.lastStickyColumn : ""}`
                                  : `${cell.column.tdColumnCustomizeClsName
                                      || styles.nonStickyColumnCell
                                    } ${i === numberOfColumnsToFix
                                      ? styles.firstNonStickyCell
                                      : ""
                                    }`
                                }`
                            }
                            // data-testid={`tableBodyCell-${cell.column.id || i}`}
                            style={{
                              padding: "0.5rem 1rem",
                              position:
                                i < numberOfColumnsToFix ? "sticky" : undefined,
                              left:
                                i < numberOfColumnsToFix && stickyLeftOffsets[i] != null
                                  ? `${stickyLeftOffsets[i]}px`
                                  : undefined,
                              zIndex: i < numberOfColumnsToFix ? 1 : undefined,
                              backgroundColor: "#fff",
                              ...cell.column.tdHeaderStyle,
                            }}
                          >
                            <div>{cell.render("Cell")}</div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr className={styles.noDataAvailableClass}>
                  <td
                    colSpan={
                      headerGroups.reduce(
                        (count, headerGroup) =>
                          count + headerGroup.headers.length,
                        0
                      ) + (isSlNoRequired ? 1 : 0) + (checkboxConfig ? 1 : 0)
                    }
                    className={`${ColumnCustomizeClsName} projectemty text-center`}
                  >
                    {noDataMessage || "No Data Available"}
                  </td>
                </tr>
              )}
            </tbody>
            {!isLoading &&
              rows.length !== 0 &&
              headerGroups.some((headerGroup) =>
                headerGroup.headers.some((column) => column.footer)
              ) && (
                <tfoot className={styles["sticky-footer"]}>
                  <tr>
                    {headerGroups.slice(-1).map((headerGroup) => {
                      return (
                        <>
                          {isSlNoRequired && (
                            <td
                              className="text-center"
                              style={{
                                position: "sticky",
                                left: "0px",
                                backgroundColor: "#dcdfdd",
                                zIndex: 1000,
                              }}
                            >
                              Total
                            </td>
                          )}
                          {checkboxConfig && (
                            <td
                              className={`text-center ${styles["th-checkbox"]}${numberOfColumnsToFix > 0
                                  ? ` ${styles["th-checkbox-fixed"]} ${isSlNoRequired
                                    ? styles["th-checkbox-with-slno"]
                                    : styles["th-checkbox-without-slno"]
                                  }`
                                  : ""
                                }`}
                            >
                            </td>
                          )}
                          {headerGroup.headers.map((column, i) => (
                            <th
                              key={column.id || i}
                              colSpan={
                                column.columns ? column.columns.length : 1
                              }
                              className={`${column.tdClassName} ${i < numberOfColumnsToFix
                                  ? styles.stickyColumnHeader
                                  : styles.nonStickyColumnHeader
                                } ${i === numberOfColumnsToFix
                                  ? styles.firstNonStickyHeader
                                  : ""
                                }`}
                              style={{
                                position:
                                  i < numberOfColumnsToFix
                                    ? "sticky"
                                    : undefined,
                                left:
                                  i < numberOfColumnsToFix && stickyLeftOffsets[i] != null
                                    ? `${stickyLeftOffsets[i]}px`
                                    : undefined,
                                zIndex: 1000,
                                backgroundColor: "#dcdfdd",
                                paddingTop: "12px",
                                paddingBottom: "12px",
                                ...column.thHeaderStyle,
                                cursor:
                                  !column.disableSortBy && headerSortingState
                                    ? "pointer"
                                    : "default",
                              }}
                            >
                              {column.footer !== undefined &&
                                column.footer !== null
                                ? column.footer
                                : column.footer === 0
                                  ? 0
                                  : ""}
                            </th>
                          ))}
                        </>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
          </table>
        </div>
        <PaginationControls
          listCount={listCount}
          isLoading={isLoading}
          curentPageNumber={curentPageNumber}
          pageLimit={pageLimit}
          gotoPage={gotoPage}
          onPageNoChange={onPageNoChange}
          previousPage={previousPage}
          nextPage={nextPage}
          setPageSize={setPageSize}
          onPageLimitChange={onPageLimitChange}
        />
      </div>
    </>
  );
}
export default React.memo(CompleteTableData);
CompleteTableData.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.node.isRequired,
      accessor: PropTypes.string.isRequired,
    })
  ).isRequired,
  isMailRequired: PropTypes.bool,
  rowCondt: PropTypes.bool,
  grid: PropTypes.bool,
  isCisoButton: PropTypes.bool,
  isVM: PropTypes.bool,
  isSHUB: PropTypes.bool,
  isCompliance: PropTypes.bool,
  isAccessReview: PropTypes.bool,
  reviewCondt: PropTypes.bool,
  pageNo: PropTypes.number.isRequired,
  moveFilterToStart: PropTypes.string.isRequired,
  DropData: PropTypes.string.isRequired,
  customSearchClass: PropTypes.string.isRequired,
  rowWidth: PropTypes.string.isRequired,
  isLoading: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  issearch: PropTypes.string.isRequired,
  onScroll: PropTypes.string.isRequired,
  moveSearchToEnd: PropTypes.string.isRequired,
  onPageNoChange: PropTypes.string.isRequired,
  onPageLimitChange: PropTypes.string.isRequired,
  headerSortingState: PropTypes.string.isRequired,
  onSorting: PropTypes.string.isRequired,
  pageLimit: PropTypes.string.isRequired,
  curentPageNumber: PropTypes.string.isRequired,
  onSearch: PropTypes.string.isRequired,
  searchKey: PropTypes.string.isRequired,
  btnData: PropTypes.string.isRequired,
  filtersToShow: PropTypes.string.isRequired,
  showFilter: PropTypes.string.isRequired,
  selectedFilterValues: PropTypes.string.isRequired,
  tbodyClass: PropTypes.string.isRequired,
  onFilter: PropTypes.string.isRequired,
  isTableFilterDisabled: PropTypes.string.isRequired,
  additionalBtnData: PropTypes.string.isRequired,
  scrollClass: PropTypes.string.isRequired,
  filterOptionToHide: PropTypes.string.isRequired,
  ismore: PropTypes.string.isRequired,
  optionList: PropTypes.string.isRequired,
  handleOptionClick: PropTypes.string.isRequired,
  selectedOption: PropTypes.string.isRequired,
  isApply: PropTypes.string.isRequired,
  filterclear: PropTypes.string.isRequired,
  requiredOptions: PropTypes.string.isRequired,
  isAllEmpty: PropTypes.string.isRequired,
  highlightRow: PropTypes.string.isRequired,
  selectedRow: PropTypes.string.isRequired,
  isNotShowMoreFilter: PropTypes.string.isRequired,
  isSlNoRequired: PropTypes.string.isRequired,
  manageBtnData: PropTypes.string.isRequired,
  removeHoveringEffect: PropTypes.string.isRequired,
  disableSorting: PropTypes.string.isRequired,
  numberOfColumnsToFix: PropTypes.string.isRequired,
  UARSearch: PropTypes.string.isRequired,
  removePosition: PropTypes.string.isRequired,
  placement: PropTypes.string.isRequired,
  tableStyle: PropTypes.string.isRequired,
  tableHeaderStyle: PropTypes.string.isRequired,
  ColumnCustomizeClsName: PropTypes.string.isRequired,
  noDataMessage: PropTypes.string.isRequired,
  TableTitle: PropTypes.element,
  listCount: PropTypes.number.isRequired,
  checkboxConfig: PropTypes.shape({
    isHeaderChecked: PropTypes.bool,
    isHeaderDisabled: PropTypes.bool,
    onSelectAll: PropTypes.func,
    isRowSelectable: PropTypes.func,
    isRowChecked: PropTypes.func,
    onRowSelect: PropTypes.func,
    headerTooltip: PropTypes.string,
    showHeaderTooltip: PropTypes.bool,
  }),
};
